import React from 'react';
import { useIntl } from 'react-intl';
import { youtubeWatchUrl } from 'fym-shared/src/music24/addedAssets';
import { TypedMemo } from '../../../../common/types';
import type { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { DirectedVideoEntry } from '../../../../directedVideo/entry/DirectedVideoEntry';
import { useDirectedVideoEntry } from '../../../../directedVideo/entry/useDirectedVideoEntry';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { downloadMediaUrl } from '../../inlineMediaUrl';
import { addedAssetsMessages } from '../../addedAssets/addedAssetsMessages';
import { sourceOfAsset } from '../../addedAssets/addedAssetFacts';
import type { AddAssetTab } from '../../addedAssets/addAssetDialogStore';
import { kitMessages } from './kitMessages';
import { KitSlotView } from './kitSlots';
import { KitActions } from './useKitActions';
import type { SlotChannel } from './KitSlotFoot';
import { WatermarkDownloadLink } from '../../../watermarkUpsell/choice/WatermarkDownloadLink';
import { choiceFileOfKitAsset } from '../../../watermarkUpsell/files/choiceFiles';
import { fileHasWatermark } from '../../../watermarkUpsell/files/watermarkedFile';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Kit.module.scss';
import { trackAssetDownloaded } from '../../assetEvents';
/* eslint-enable css-modules/no-unused-class */

interface KitMusicVideoFootProps {
  accountId: number;
  item: CatalogItem;
  view: KitSlotView;
  channels: SlotChannel[];
  actions: KitActions;
  onAdd: (tab?: AddAssetTab) => void;
  onOpenEditor: (cut?: string | null) => void;
  onRemove: () => void;
  onPublish: (entry: SlotChannel) => void;
  /** The slot name and the song title: the choice of a watermarked download names them (trial-upsell a2). */
  name?: string;
  songTitle?: string;
}

/**
 * The actions of the Music video slot (design music-video a1, a5, a6; spec 2026-10-09 D1, D11):
 * - not made: "Add your video" and "Make one" (the editor). Never "Make it · 3 credits": the
 *   one-shot music video is gone.
 * - a linked YouTube video (a5): "Open on YouTube", "Change", "Remove".
 * - an uploaded file (a6): "Publish", "Make posts from it" (the Studio), "Download", "Remove".
 * - made in the editor: "Publish", "Edit the video" (the editor with that cut), "Download".
 * - the directed video beta (design directed-video a1, a2; owner report 2026-10-20): with
 *   `offersDirectedVideo` the not-made slot shows "Directed video · Beta" as the primary action, and
 *   "Add your video" and "Make one" as secondary actions. An open directed video shows its step and
 *   "Continue", or its progress and "See the progress", in every state of the slot. No design shows
 *   a Kit card with three actions, so the directed video is the primary action. Without the rule
 *   the slot stays as before: no lock, no tease.
 */
const KitMusicVideoFootComponent: React.FC<KitMusicVideoFootProps> = (props) => {
  const { accountId, item, view, channels, actions, onAdd, onOpenEditor, onRemove, onPublish, name, songTitle } = props;
  const intl = useIntl();
  const asset = view.readyAsset;
  const directed = useDirectedVideoEntry({
    accountId,
    catalogItemUuid: item.uuid,
    song: {
      hasMaster: item.song.hasMaster,
      masterReady: item.song.masterStatus === 'ready',
      hasLyrics: item.song.hasLyrics,
    },
  });
  const directedEntry =
    directed.visible && actions.canAct ? (
      <div className={styles.directed} data-testid="kit-directed-video">
        <DirectedVideoEntry
          open={directed.open}
          readOnly={false}
          onStart={directed.start}
          needsPlan={directed.needsPlan}
          primary
        />
      </div>
    ) : null;
  // A made slot shows only an open directed video (its step or its progress), not a new one.
  const directedRun = directed.open === null ? null : directedEntry;

  if (view.state === 'making') {
    return directedRun === null ? null : <div className={styles.foot}>{directedRun}</div>;
  }
  if (asset === null || view.state !== 'made') {
    return actions.canAct ? (
      <div className={styles.foot}>
        {directedEntry}
        <div className={styles.twoButtons}>
          <SMLButton onClick={() => onAdd()}>{intl.formatMessage(addedAssetsMessages.addYourVideo)}</SMLButton>
          <SMLButton variant={directed.visible ? 'default' : 'primary'} onClick={() => onOpenEditor()}>
            {intl.formatMessage(addedAssetsMessages.makeOne)}
          </SMLButton>
        </div>
      </div>
    ) : null;
  }

  const source = sourceOfAsset(asset);
  if (source === 'youtube') {
    return (
      <div className={styles.foot}>
        {directedRun}
        {asset.youtube !== undefined && asset.youtube !== null && (
          <a className={styles.link} href={youtubeWatchUrl(asset.youtube.videoId)} target="_blank" rel="noreferrer">
            {intl.formatMessage(addedAssetsMessages.openOnYouTube)}
          </a>
        )}
        <span className={styles.grow} />
        {actions.canAct && (
          <>
            <button type="button" className={styles.link} onClick={() => onAdd('youtube')}>
              {intl.formatMessage(addedAssetsMessages.change)}
            </button>
            <button type="button" className={styles.link} onClick={onRemove}>
              {intl.formatMessage(addedAssetsMessages.remove)}
            </button>
          </>
        )}
      </div>
    );
  }

  const publishable = channels[0];
  return (
    <div className={styles.foot}>
      {directedRun}
      {actions.canAct && publishable !== undefined && (
        <button type="button" className={styles.link} onClick={() => onPublish(publishable)}>
          {intl.formatMessage(kitMessages.publish)}
        </button>
      )}
      {actions.canAct && source === 'upload' && (
        <button type="button" className={styles.link} onClick={actions.openStudio}>
          {intl.formatMessage(addedAssetsMessages.makePosts)}
        </button>
      )}
      {actions.canAct && source === 'made' && (
        <button
          type="button"
          className={styles.link}
          onClick={() => onOpenEditor(asset.params.musicVideoCutUuid ?? null)}
        >
          {intl.formatMessage(addedAssetsMessages.editVideo)}
        </button>
      )}
      <span className={styles.grow} />
      {actions.canAct && source === 'upload' && (
        <button type="button" className={styles.link} onClick={onRemove}>
          {intl.formatMessage(addedAssetsMessages.remove)}
        </button>
      )}
      {asset.fileUrl !== null && (
        <WatermarkDownloadLink
          className={styles.link}
          href={downloadMediaUrl(asset.fileUrl)}
          hasWatermark={fileHasWatermark(asset)}
          file={choiceFileOfKitAsset(asset, name ?? null, songTitle ?? null)}
          onDownload={() => trackAssetDownloaded(asset.themeId, 'kit')}
        >
          {intl.formatMessage(kitMessages.download)}
        </WatermarkDownloadLink>
      )}
    </div>
  );
};

export const KitMusicVideoFoot = TypedMemo(KitMusicVideoFootComponent);
